
import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Loader2, SendHorizontal } from 'lucide-react';

interface ReplyFormProps {
  threadId: string;
}

const ReplyForm: React.FC<ReplyFormProps> = ({ threadId }) => {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim() || !user) {
      toast({
        title: "Missing information",
        description: "Please provide content for your reply",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setSubmitting(true);
      
      const { error } = await supabase
        .from("club_thread_replies")
        .insert({
          thread_id: threadId,
          content: content.trim(),
          user_id: user.id
        });
        
      if (error) throw error;
      
      toast({
        title: "Reply posted",
        description: "Your reply has been added to the discussion"
      });
      
      setContent("");
      
    } catch (error) {
      console.error("Error creating reply:", error);
      toast({
        title: "Error",
        description: "Failed to post reply",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="py-4">
          <p className="text-center text-muted-foreground">
            Sign in to join the discussion
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <CardContent className="pt-4">
          <Textarea 
            placeholder="Write your reply here..."
            className="min-h-[100px] resize-y"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={submitting}
          />
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button 
            type="submit" 
            disabled={submitting || !content.trim()}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <SendHorizontal className="h-4 w-4 mr-2" />
            )}
            Post Reply
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default ReplyForm;
